<script setup>
import { reactive, ref } from "vue";

// URL del backend (tu enviar.php de citas)
const API_URL = "https://clinicasagradafamilia.net/formulario-csf/enviar.php";

/* -----------------------------
   Estado del formulario
--------------------------------*/
const form = reactive({
  nombre: "",
  cedula: "",
  email: "",
  telefono: "",
  direccion: "",
  fechanat: "",
  especialidad: "",
  imagenologia: "",
  mensaje: "",
});

const pdfFile = ref(null);
const aceptaTerminos = ref(false);

const sending = ref(false);
const errors = ref([]);

// para el modal de éxito
const showSuccessModal = ref(false);

/* -----------------------------
   Manejo de archivo PDF
--------------------------------*/
function onFileChange(event) {
  // borro errores anteriores del PDF
  errors.value = errors.value.filter((e) => !e.startsWith("PDF:"));

  const file = event.target.files?.[0] || null;
  pdfFile.value = null;

  if (!file) return;

  if (file.type !== "application/pdf") {
    errors.value.push("PDF: Solo se aceptan archivos en formato PDF.");
    event.target.value = "";
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    errors.value.push("PDF: El archivo no debe superar los 5 MB.");
    event.target.value = "";
    return;
  }

  pdfFile.value = file;
}

/* -----------------------------
   Validación
--------------------------------*/
function validate() {
  const e = [];

  if (!form.nombre) e.push("El nombre es obligatorio.");
  if (!form.cedula) e.push("La cédula es obligatoria.");
  if (!form.telefono) e.push("El teléfono es obligatorio.");
  if (!form.direccion) e.push("La dirección es obligatoria.");
  if (!form.fechanat) e.push("La fecha de nacimiento es obligatoria.");

  if (!form.email) {
    e.push("El correo electrónico es obligatorio.");
  } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
    e.push("El correo electrónico no es válido.");
  }

  // El backend actual exige AMBAS selecciones
  if (!form.especialidad) e.push("Debes seleccionar una especialidad.");
  if (!form.imagenologia)
    e.push("Debes seleccionar un servicio de imagenología.");

  if (!form.mensaje) e.push("El mensaje es obligatorio.");

  if (!aceptaTerminos.value) {
    e.push("Debes aceptar la política de tratamiento de datos.");
  }

  errors.value = e;
  return e.length === 0;
}

/* -----------------------------
   Envío del formulario
--------------------------------*/
async function submitForm() {
  errors.value = [];

  if (!validate()) return;
  sending.value = true;

  try {
    const fd = new FormData();

    Object.entries(form).forEach(([k, v]) => {
      fd.append(k, v ?? "");
    });

    // Campo que espera el backend
    fd.append("acceptTerms", aceptaTerminos.value ? "1" : "0");

    if (pdfFile.value) {
      fd.append("pdf", pdfFile.value);
    }

    const res = await fetch(API_URL, {
      method: "POST",
      body: fd,
    });

    const data = await res.json().catch(() => null);
    console.log("Respuesta del backend:", data);

    if (!res.ok || !data || data.ok === false) {
      if (data && Array.isArray(data.errors) && data.errors.length) {
        errors.value = data.errors;
      } else if (data && data.message) {
        errors.value = [data.message];
      } else {
        errors.value = ["Error enviando el formulario."];
      }
      return;
    }

    // ✅ OK → mostrar modal
    showSuccessModal.value = true;

    // Reset de formulario
    Object.assign(form, {
      nombre: "",
      cedula: "",
      email: "",
      telefono: "",
      direccion: "",
      fechanat: "",
      especialidad: "",
      imagenologia: "",
      mensaje: "",
    });
    aceptaTerminos.value = false;
    pdfFile.value = null;
    const fileInput = document.getElementById("pdf-cita");
    if (fileInput) fileInput.value = "";
    errors.value = [];
  } catch (err) {
    console.error("Error al enviar formulario:", err);
    errors.value = [
      "No se pudo enviar. Revisa tu conexión o intenta más tarde.",
    ];
  } finally {
    sending.value = false;
  }
}

/* -----------------------------
   Cerrar modal
--------------------------------*/
function closeSuccessModal() {
  showSuccessModal.value = false;
}
</script>

<template>
  <!-- HERO -->
  <section class="pb-5 relative block bg-blueGray-800">
    <div class="container mx-auto px-4 lg:pt-24 lg:pb-64">
      <div class="flex flex-wrap text-center justify-center">
        <div class="w-full lg:w-6/12 px-4">
          <br /><br /><br />
          <h2 class="text-4xl font-semibold text-otherblue">Citas en Línea</h2>
        </div>
      </div>
    </div>
  </section>

  <!-- FORMULARIO -->
  <section class="relative block pt-24 lg:pt-0 bg-blueGray-800">
    <div class="container mx-auto px-4">
      <div class="flex flex-wrap justify-center lg:-mt-64 -mt-48">
        <div class="w-full lg:w-7/12 px-4">
          <div
            class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100"
          >
            <div class="flex-auto p-6 lg:p-10">
              <!-- Errores -->
              <div
                v-if="errors.length"
                class="mb-5 p-4 bg-red-50 border border-red-300 text-red-600 rounded-md"
              >
                <ul class="list-disc list-inside space-y-1">
                  <li v-for="(error, i) in errors" :key="i">⚠️ {{ error }}</li>
                </ul>
              </div>

              <div class="space-y-6">
                <!-- DATOS PERSONALES -->
                <section class="space-y-3">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Nombre completo *</label
                      >
                      <input
                        v-model.trim="form.nombre"
                        type="text"
                        placeholder="Ingrese su nombre completo"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Número de cédula *</label
                      >
                      <input
                        v-model.trim="form.cedula"
                        type="text"
                        placeholder="Ingrese su número de cédula"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Teléfono *</label
                      >
                      <input
                        v-model.trim="form.telefono"
                        type="tel"
                        placeholder="Ingrese su número de teléfono"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Dirección *</label
                      >
                      <input
                        v-model.trim="form.direccion"
                        type="text"
                        placeholder="Ingrese su dirección"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Fecha de nacimiento *</label
                      >
                      <input
                        v-model="form.fechanat"
                        type="date"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Correo electrónico *</label
                      >
                      <input
                        v-model.trim="form.email"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      />
                    </div>
                  </div>
                </section>

                <!-- INFORMACIÓN DE LA CITA -->
                <section class="space-y-3">
                  <h4 class="text-sm font-bold uppercase text-gray-500">
                    Información de la cita
                  </h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Especialidad *</label
                      >
                      <select
                        v-model="form.especialidad"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      >
                        <option value="" disabled>
                          Seleccione la especialidad
                        </option>
                        <option value="Algesiología">Algesiología</option>
                        <option value="Anestesiología">Anestesiología</option>
                        <option value="Cardiología">Cardiología</option>
                        <option value="Cirugía Cardiovascular">
                          Cirugía Cardiovascular
                        </option>
                        <option value="Cirugía de mano">Cirugía de mano</option>
                        <option value="Cirugía de pie">Cirugía de pie</option>
                        <option value="Cirugía de Tórax">
                          Cirugía de Tórax
                        </option>
                        <option value="Cirugía general">Cirugía general</option>
                        <option value="Cirugía Bariátrica">
                          Cirugía Bariátrica
                        </option>
                        <option value="Cirugía plástica">
                          Cirugía plástica
                        </option>
                        <option value="Cirugía pediátrica">
                          Cirugía pediátrica
                        </option>
                        <option value="Cirugía maxilofacial">
                          Cirugía maxilofacial
                        </option>
                        <option value="Cirugía vascular">
                          Cirugía vascular
                        </option>
                        <option value="Dermatología">Dermatología</option>
                        <option value="Electrofisiología">
                          Electrofisiología
                        </option>
                        <option value="Endocrinología (Adulto y pediátrica)">
                          Endocrinología (Adulto y pediátrica)
                        </option>
                        <option value="Gineco – obstetricia">
                          Gineco – obstetricia
                        </option>
                        <option value="Ginecología">Ginecología</option>
                        <option value="Hematología pediátrica">
                          Hematología pediátrica
                        </option>
                        <option value="Perinatología">Perinatología</option>
                        <option value="Medicina General">
                          Medicina General
                        </option>
                        <option value="Medicina interna">
                          Medicina interna
                        </option>
                        <option value="Neurocirugía">Neurocirugía</option>
                        <option value="Neurología">Neurología</option>
                        <option value="Nutrición">Nutrición</option>
                        <option value="Ortopedia">Ortopedia</option>
                        <option value="Ortopedia Artroscópica">
                          Ortopedia Artroscópica
                        </option>
                        <option value="Otorrinolaringología">
                          Otorrinolaringología
                        </option>
                        <option value="Otología">Otología</option>
                        <option value="Pediatría">Pediatría</option>
                        <option value="Psicología">Psicología</option>
                        <option value="Psiquiatría">Psiquiatría</option>
                        <option
                          value="Terapia: Física, respiratoria y lenguaje"
                        >
                          Terapia: Física, respiratoria y lenguaje
                        </option>
                        <option value="Urología (Adulto y pediátrica)">
                          Urología (Adulto y pediátrica)
                        </option>
                      </select>
                    </div>

                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Imagenología *</label
                      >
                      <select
                        v-model="form.imagenologia"
                        class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                      >
                        <option value="" disabled>
                          Seleccione el servicio
                        </option>
                        <option value="Ecografía">Ecografía</option>
                        <option value="Ecografía obstétrica">
                          Ecografía obstétrica
                        </option>
                        <option value="Ecocardiograma transtorácico">
                          Ecocardiograma transtorácico
                        </option>
                        <option value="Radiografía (RX)">
                          Radiografía (RX)
                        </option>
                        <option value="Resonancia Magnética">
                          Resonancia Magnética
                        </option>
                        <option value="Tomografía Axial Computarizada">
                          Tomografía Axial Computarizada
                        </option>
                      </select>
                    </div>
                  </div>
                </section>

                <!-- PDF -->
                <section class="space-y-3">
                  <h4 class="text-sm font-bold uppercase text-gray-500">
                    Adjuntos
                  </h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        class="font-semibold text-xs text-gray-600 uppercase"
                        >Orden médica / soporte (PDF, opcional)</label
                      >
                      <input
                        id="pdf-cita"
                        type="file"
                        accept=".pdf"
                        @change="onFileChange"
                        class="p-2 px-2 border-b-2 bg-white w-full outline-none text-gray-800"
                      />
                    </div>
                  </div>
                </section>

                <!-- MENSAJE -->
                <section class="space-y-3">
                  <h4 class="text-sm font-bold uppercase text-gray-500">
                    Detalle de la solicitud
                  </h4>
                  <div>
                    <label class="font-semibold text-xs text-gray-600 uppercase"
                      >Mensaje *</label
                    >
                    <textarea
                      v-model.trim="form.mensaje"
                      rows="4"
                      placeholder="Describa brevemente su solicitud"
                      class="p-2 px-2 border-b-2 w-full outline-none text-gray-800 bg-transparent"
                    ></textarea>
                  </div>
                </section>

                <!-- TÉRMINOS -->
                <section class="space-y-2">
                  <label class="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      v-model="aceptaTerminos"
                      class="mt-1"
                    />
                    <span class="text-gray-700">
                      Acepto la
                      <a
                        href="https://www.clinicasagradafamilia.net/Politica-de-Tratamiento-de-la-Informacion"
                        target="_blank"
                        class="underline text-otherblue"
                      >
                        Política de tratamiento de datos y de la información
                      </a>
                      de la Clínica Sagrada Familia.
                    </span>
                  </label>
                </section>

                <!-- BOTÓN -->
                <section class="pt-2">
                  <button
                    :disabled="sending"
                    @click="submitForm"
                    class="py-3 bg-otherblue text-white w-full rounded hover:bg-graynefro disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {{ sending ? "Enviando..." : "Enviar solicitud" }}
                  </button>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE ÉXITO -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        <div class="bg-green-500 text-white px-6 py-3 flex items-center gap-2">
          <span class="text-xl">✔</span>
          <h4 class="font-semibold">Solicitud enviada</h4>
        </div>
        <div class="p-6 text-center">
          <p class="text-gray-800 text-lg mb-4">
            ¡Tu solicitud de cita fue enviada correctamente!
          </p>
          <p class="text-sm text-gray-600 mb-6">
            En breve nuestro equipo revisará la información y se pondrá en
            contacto contigo a través de los datos registrados.
          </p>
          <button
            @click="closeSuccessModal"
            class="px-6 py-2 rounded bg-otherblue text-white hover:bg-graynefro"
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Ajustes opcionales adicionales */
</style>
